import { encodeCardAsBase64 } from '@/shared/card-encoding';
import axios from 'axios';
// import { Base64 } from 'js-base64';
import * as log from 'loglevel';
import overrides from '@/shared/card-formats/overrides';

// import getter types which are used inside the actions
import {
  SIDE_IDS,
  PRINT_SHOP_NOTES_BY_SIDE_ID,
  CARD_ID,
  SESSION_ID,
  PAPER_ID,
} from '@/store/getter-types';

// import mutation types which are used inside the actions
import {
  SET_CARD,
  SET_ACTIVE_SIDE_TO_FIRST_SIDE,
  SET_ACTIVE_SIDE_BY_ID,
  SET_ACTIVE_LAYER_BY_ID,
  SET_ACTIVE_LAYER_TO_NONE,
  SET_FONT_SIZE_FOR_ACTIVE_LAYER,
  SET_LINE_HEIGHT_FOR_ACTIVE_LAYER,
  ACTIVATE_THEME_BY_ID,
  SET_TOOLTIPS_TO_VISIBLE,
  SET_TOOLTIPS_TO_NOT_VISIBLE,
  SET_PREVIEW_VISIBILITY,
  SET_PREVIEW_IMAGES,
  SET_PREVIEW_IMAGES_LOADED,
  GENERATE_SESSION_ID,
  SET_LAYOUT_MODE,
  TAKE_CARD_OBJECT_SNAPSHOT,
  RESTORE_CARD_OBJECT_SNAPSHOT,
  SET_PAPER_ID,
} from '@/store/mutation-types';

import {
  LOAD_CARD,
  ACTIVATE_SIDE_BY_ID,
  ACTIVATE_LAYER_BY_ID,
  DEACTIVATE_LAYER,
  SET_FONT_SIZE,
  SET_LINE_HEIGHT,
  SHOW_TOOLTIPS,
  HIDE_TOOLTIPS,
  TOGGLE_TOOLTIPS,
  SHOW_PREVIEW,
  REQUEST_PDF,
  REQUEST_SCREENSHOT_OF_EACH_SIDE,
  ACTIVATE_LAYOUT_MODE,
  DISCARD_LAYOUT_CHANGES,
  // CREATE_GLOBAL_EVENT_LISTENERS,
} from '@/store/action-types';

export default {
  /**
   * Loads a card and initializes it
   * @param {Object} context
   * @param {Object} payload
   * @returns {Promise}
   */
  [LOAD_CARD]({ commit }, { card, themeId, paperId }) {
    return new Promise((resolve) => {
      // before commiting the card we have to check if there is an override
      // and if so apply it
      const override = overrides.hasOwnProperty(card.formatId) ? overrides[card.formatId] : null;
      if (override) {
        if (override.sides) {
          override.sides.forEach((sideOverride) => {
            const originalSide = card.sides.find(side => side.id === sideOverride.id);
            if (originalSide) {
              // here we hand write all properties that we might overwrite
              if (sideOverride.shifts) {
                originalSide.shifts = { ...sideOverride.shifts };
              }
            } else {
              log.error('The card format has an override, but it could not be applied to the card. At least one side of the card override is not present in the current card.');
              log.error('Card: ', card);
              log.error('Card override: ', override);
            }
          });
        }
      }
      commit(SET_CARD, card);
      // There are two use-cases:
      // 1) Card is loaded directly from shop and themeId is passed
      //    (which color theme has been activated by the user)
      // 2) Card is loaded from wishlist, then "activeThemeId" is being used inside the card object
      let themeIdToLoad = null;
      if (themeId) {
        themeIdToLoad = themeId;
      } else { // not themeId passed, let’s use "activeThemeId" from the card object
        themeIdToLoad = card.activeThemeId;
      }
      if (themeIdToLoad) {
        // check if theme ID exists in themes array
        if (card.themes.some(t => t.id === themeIdToLoad)) {
          commit(ACTIVATE_THEME_BY_ID, themeIdToLoad);
        }
      }
      if (paperId) {
        commit(SET_PAPER_ID, paperId);
      }
      if (!card.sessionId) {
        commit(GENERATE_SESSION_ID);
      }
      commit(SET_ACTIVE_SIDE_TO_FIRST_SIDE);
      resolve();
    });
  },

  [ACTIVATE_SIDE_BY_ID]({ commit }, sideId) {
    commit(SET_ACTIVE_LAYER_BY_ID, ''); // de-select
    commit(SET_ACTIVE_SIDE_BY_ID, sideId);
  },

  [ACTIVATE_LAYER_BY_ID]({ commit }, layerId) {
    commit(SET_ACTIVE_LAYER_BY_ID, layerId);
  },

  [DEACTIVATE_LAYER]({ commit }) {
    commit(SET_ACTIVE_LAYER_TO_NONE);
  },

  [SET_FONT_SIZE]({ commit }, value) {
    commit(SET_FONT_SIZE_FOR_ACTIVE_LAYER, value);
  },

  [SET_LINE_HEIGHT]({ commit }, value) {
    commit(SET_LINE_HEIGHT_FOR_ACTIVE_LAYER, value);
  },

  [SHOW_TOOLTIPS]({ commit }) {
    commit(SET_TOOLTIPS_TO_VISIBLE);
  },

  [HIDE_TOOLTIPS]({ commit }) {
    commit(SET_TOOLTIPS_TO_NOT_VISIBLE);
  },

  [TOGGLE_TOOLTIPS]({ state, dispatch }) {
    if (state.tmp.tooltipsVisible) {
      dispatch(HIDE_TOOLTIPS);
    } else {
      dispatch(SHOW_TOOLTIPS);
    }
  },

  [REQUEST_SCREENSHOT_OF_EACH_SIDE]() {
    // call kartendesigner-services with the config-object of this card and
    // take screenshot of each card (await!?)
    // ...
    // For now, send dummy pictures
    // simulate request
    // setTimeout(() => {
    //   commit('SET_PREVIEW_IMAGE_FOR_SIDE', {

    //   });
    // }, 500);

  },

  [SHOW_PREVIEW]({ state, commit, getters }) {
    // request screenshots
    // commit(SHOW_PREVIEW);
    // TODO: Later on this could be optimized:
    //   - Remember when the last screenshot of each side of this card was taken
    //   - Decide if it is really necessary to re-render it (changed made?)
    //   - Only the re-render the screenshot(s)
    //   - Check for each side
    // The check could work like this:
    //   - When preview was pressed, create hash of data-object for each side
    //   - Store hast in data-object as 'lastHash'
    //   - Generate new hash and compare it with old one
    commit(SET_PREVIEW_IMAGES_LOADED, false);
    const cardBase64Encoded = encodeCardAsBase64(state.card);
    log.debug('Card: ', state.card);
    log.debug('Card as BASE64: ', cardBase64Encoded);

    axios.post(`${process.env.VUE_APP_IMAGE_SERVER}/generate-screenshots`, {
      url: process.env.VUE_APP_KARTENDESIGNER_URL,
      card: cardBase64Encoded,
      sideIds: getters[SIDE_IDS],
      notes: getters[PRINT_SHOP_NOTES_BY_SIDE_ID],
      cardId: getters[CARD_ID],
      sessionId: getters[SESSION_ID],
    }).then((resp) => {
      if (resp && resp.data && resp.data.sides) {
        const previewImages = {};
        resp.data.sides.forEach((side) => {
          previewImages[side.sideId] = side.imageUrl;
        });
        commit(SET_PREVIEW_IMAGES, previewImages);
        commit(SET_PREVIEW_IMAGES_LOADED, true);
      }
    }).catch((error) => {
      log.error('There was an error generating the screenshots', error);
      commit(SET_PREVIEW_VISIBILITY, false);
      alert('Entschuldigung! Leider gibt es zur Zeit technische Probleme. Bitte versuchen Sie es später noch ein Mal.'); // TODO: Later on design and implement nice looking alert replacement
    });
    commit(SET_PREVIEW_VISIBILITY, true);
  },

  [REQUEST_PDF]({ state, commit, getters }) {
    return new Promise((resolve, reject) => {
      commit(SET_PREVIEW_IMAGES_LOADED, false);
      axios.post(`${process.env.VUE_APP_IMAGE_SERVER}/generate-pdf`, {
        url: process.env.VUE_APP_KARTENDESIGNER_URL,
        card: encodeCardAsBase64(state.card),
        sideIds: getters[SIDE_IDS],
        notes: getters[PRINT_SHOP_NOTES_BY_SIDE_ID],
        cardId: getters[CARD_ID],
        paperId: getters[PAPER_ID],
        sessionId: getters[SESSION_ID],
      }).then((resp) => {
        resolve(resp);
        // if (resp && resp.data && resp.data.sides) {
        //   const previewImages = {};
        //   resp.data.sides.forEach((side) => {
        //
        //     previewImages[side.sideId] = side.imageUrl;
        //   });
        //   commit(SET_PREVIEW_IMAGES, previewImages);
        //   commit(SET_PREVIEW_IMAGES_LOADED, true);
        // }
      }).catch((error) => {
        reject(error);
      });
      // commit(SET_PREVIEW_VISIBILITY, true);
    });
  },

  [ACTIVATE_LAYOUT_MODE]({ commit }) {
    log.debug('Activate layout mode');
    commit(SET_ACTIVE_LAYER_TO_NONE);
    commit(TAKE_CARD_OBJECT_SNAPSHOT);
    commit(SET_LAYOUT_MODE, true);
  },

  [DISCARD_LAYOUT_CHANGES]({ commit }) {
    log.debug('Discard layout changes');
    commit(SET_LAYOUT_MODE, false);
    commit(RESTORE_CARD_OBJECT_SNAPSHOT);
  },
};
